import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { creativesService } from '../../services/adminService';

interface Props {
  programId: string | null | undefined;
}

interface BannerPayload {
  id?: string;
  name: string;
  image_url: string;
  target_url?: string;
  active?: boolean;
  tags?: string[];
}

interface FeedPayload {
  id?: string;
  name: string;
  url: string;
  format?: 'csv' | 'json' | 'xml';
  active?: boolean;
}

interface CouponPayload {
  id?: string;
  code: string;
  description?: string;
  discount_type?: 'percent' | 'amount';
  discount_value?: number;
  active?: boolean;
  starts_at?: string;
  ends_at?: string;
}

const CreativesManagement: React.FC<Props> = ({ programId }) => {
  const pid = useMemo(() => programId || '', [programId]);
  const [loading, setLoading] = useState(false);

  // Banners state
  const [banners, setBanners] = useState<any[]>([]);
  const [bannerForm, setBannerForm] = useState<BannerPayload>({ name: '', image_url: '', target_url: '', active: true });

  // Feeds state
  const [feeds, setFeeds] = useState<any[]>([]);
  const [feedForm, setFeedForm] = useState<FeedPayload>({ name: '', url: '', format: 'csv', active: true });

  // Link builder
  const [baseUrl, setBaseUrl] = useState('');
  const [affiliateId, setAffiliateId] = useState('');
  const [campaign, setCampaign] = useState('');
  const [coupon, setCoupon] = useState('');
  const [linkResult, setLinkResult] = useState<string>('');

  // Coupons
  const [coupons, setCoupons] = useState<any[]>([]);
  const [couponForm, setCouponForm] = useState<CouponPayload>({ code: '', description: '', discount_type: 'percent', discount_value: 10, active: true });

  const disabled = !pid;

  useEffect(() => {
    const loadAll = async () => {
      if (!pid) return;
      setLoading(true);
      try {
        const [b, f, c] = await Promise.all([
          creativesService.listBanners(pid),
          creativesService.listFeeds(pid),
          creativesService.listCoupons(pid),
        ]);
        setBanners(Array.isArray(b) ? b : []);
        setFeeds(Array.isArray(f) ? f : []);
        setCoupons(Array.isArray(c) ? c : []);
      } catch (e) {
        toast.error('Failed to load creatives');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [pid]);

  const handleSaveBanner = async () => {
    if (disabled) return;
    if (!bannerForm.name || !bannerForm.image_url) {
      toast.error('Banner name and image URL are required');
      return;
    }
    try {
      await creativesService.upsertBanner(pid, bannerForm);
      toast.success('Banner saved');
      const b = await creativesService.listBanners(pid);
      setBanners(b || []);
      setBannerForm({ name: '', image_url: '', target_url: '', active: true });
    } catch (e) {
      toast.error('Failed to save banner');
    }
  };

  const handleDeleteBanner = async (id: string) => {
    try {
      await creativesService.deleteBanner(pid, id);
      setBanners((prev) => prev.filter((x) => x.id !== id));
      toast.success('Banner deleted');
    } catch (e) {
      toast.error('Failed to delete banner');
    }
  };

  const handleSaveFeed = async () => {
    if (disabled) return;
    if (!feedForm.name || !feedForm.url) {
      toast.error('Feed name and URL are required');
      return;
    }
    try {
      await creativesService.upsertFeed(pid, feedForm);
      toast.success('Feed saved');
      const f = await creativesService.listFeeds(pid);
      setFeeds(f || []);
      setFeedForm({ name: '', url: '', format: 'csv', active: true });
    } catch (e) {
      toast.error('Failed to save feed');
    }
  };

  const handleDeleteFeed = async (id: string) => {
    try {
      await creativesService.deleteFeed(pid, id);
      setFeeds((prev) => prev.filter((x) => x.id !== id));
      toast.success('Feed deleted');
    } catch (e) {
      toast.error('Failed to delete feed');
    }
  };

  const handleGenerateLink = async () => {
    if (disabled) return;
    if (!baseUrl) {
      toast.error('Base URL is required');
      return;
    }
    try {
      const resp = await creativesService.generateTrackingLink(pid, {
        base_url: baseUrl,
        affiliate_id: affiliateId || undefined,
        campaign: campaign || undefined,
        coupon: coupon || undefined,
      });
      setLinkResult(resp?.url || '');
      if (resp?.url) toast.success('Tracking link generated');
    } catch (e) {
      toast.error('Failed to generate link');
    }
  };

  const handleSaveCoupon = async () => {
    if (disabled) return;
    if (!couponForm.code) {
      toast.error('Coupon code is required');
      return;
    }
    try {
      await creativesService.upsertCoupon(pid, couponForm);
      toast.success('Coupon saved');
      const list = await creativesService.listCoupons(pid);
      setCoupons(list || []);
      setCouponForm({ code: '', description: '', discount_type: 'percent', discount_value: 10, active: true });
    } catch (e) {
      toast.error('Failed to save coupon');
    }
  };

  const handleDeleteCoupon = async (id: string) => {
    try {
      await creativesService.deleteCoupon(pid, id);
      setCoupons((prev) => prev.filter((x) => x.id !== id));
      toast.success('Coupon deleted');
    } catch (e) {
      toast.error('Failed to delete coupon');
    }
  };

  if (!pid) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 p-6">
        <h3 className="text-lg font-semibold mb-2">Creatives</h3>
        <p className="text-gray-600">Create a program to manage banners, feeds, tracking links, and coupons.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Creatives</h2>
      {loading && (
        <div className="text-sm text-gray-500 mb-3">Loading…</div>
      )}

      {/* Banners */}
      <div className="rounded-lg border bg-white p-4 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Banners</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label htmlFor="banner-name" className="block text-xs text-gray-600 mb-1">Name</label>
            <input id="banner-name" className="border rounded px-3 py-2 w-full" value={bannerForm.name} onChange={(e) => setBannerForm({ ...bannerForm, name: e.target.value })} />
          </div>
          <div>
            <label htmlFor="banner-image" className="block text-xs text-gray-600 mb-1">Image URL</label>
            <input id="banner-image" className="border rounded px-3 py-2 w-full" value={bannerForm.image_url} onChange={(e) => setBannerForm({ ...bannerForm, image_url: e.target.value })} />
          </div>
          <div>
            <label htmlFor="banner-target" className="block text-xs text-gray-600 mb-1">Target URL</label>
            <input id="banner-target" className="border rounded px-3 py-2 w-full" value={bannerForm.target_url || ''} onChange={(e) => setBannerForm({ ...bannerForm, target_url: e.target.value })} />
          </div>
          <label className="inline-flex items-center gap-2 text-sm mt-6">
            <input type="checkbox" checked={!!bannerForm.active} onChange={(e) => setBannerForm({ ...bannerForm, active: e.target.checked })} />
            <span>Active</span>
          </label>
        </div>
        <div className="flex gap-2 mb-4">
          <button className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60" onClick={handleSaveBanner} disabled={disabled}>Save Banner</button>
        </div>
        <ul className="divide-y">
          {banners.length === 0 && <li className="py-2 text-sm text-gray-500">No banners yet</li>}
          {banners.map((b) => (
            <li key={b.id} className="py-2 flex items-center justify-between">
              <div className="text-sm">
                <div className="font-medium">{b.name}</div>
                <div className="text-gray-500">{b.image_url}</div>
              </div>
              <button className="px-2 py-1 rounded border text-sm" onClick={() => handleDeleteBanner(b.id)}>Delete</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Feeds */}
      <div className="rounded-lg border bg-white p-4 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Feeds</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label htmlFor="feed-name" className="block text-xs text-gray-600 mb-1">Name</label>
            <input id="feed-name" className="border rounded px-3 py-2 w-full" value={feedForm.name} onChange={(e) => setFeedForm({ ...feedForm, name: e.target.value })} />
          </div>
          <div>
            <label htmlFor="feed-url" className="block text-xs text-gray-600 mb-1">URL</label>
            <input id="feed-url" className="border rounded px-3 py-2 w-full" value={feedForm.url} onChange={(e) => setFeedForm({ ...feedForm, url: e.target.value })} />
          </div>
          <div>
            <label htmlFor="feed-format" className="block text-xs text-gray-600 mb-1">Format</label>
            <select id="feed-format" className="border rounded px-3 py-2 w-full" value={feedForm.format} onChange={(e) => setFeedForm({ ...feedForm, format: e.target.value as any })}>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="xml">XML</option>
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm mt-6">
            <input type="checkbox" checked={!!feedForm.active} onChange={(e) => setFeedForm({ ...feedForm, active: e.target.checked })} />
            <span>Active</span>
          </label>
        </div>
        <div className="flex gap-2 mb-4">
          <button className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60" onClick={handleSaveFeed} disabled={disabled}>Save Feed</button>
        </div>
        <ul className="divide-y">
          {feeds.length === 0 && <li className="py-2 text-sm text-gray-500">No feeds yet</li>}
          {feeds.map((f) => (
            <li key={f.id} className="py-2 flex items-center justify-between">
              <div className="text-sm">
                <div className="font-medium">{f.name}</div>
                <div className="text-gray-500">{f.url}</div>
              </div>
              <button className="px-2 py-1 rounded border text-sm" onClick={() => handleDeleteFeed(f.id)}>Delete</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Link Builder */}
      <div className="rounded-lg border bg-white p-4 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Link Builder</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label htmlFor="lb-base" className="block text-xs text-gray-600 mb-1">Base URL</label>
            <input id="lb-base" className="border rounded px-3 py-2 w-full" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://yourdomain.com/landing" />
          </div>
          <div>
            <label htmlFor="lb-aff" className="block text-xs text-gray-600 mb-1">Affiliate ID (optional)</label>
            <input id="lb-aff" className="border rounded px-3 py-2 w-full" value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)} />
          </div>
          <div>
            <label htmlFor="lb-campaign" className="block text-xs text-gray-600 mb-1">Campaign (optional)</label>
            <input id="lb-campaign" className="border rounded px-3 py-2 w-full" value={campaign} onChange={(e) => setCampaign(e.target.value)} />
          </div>
          <div>
            <label htmlFor="lb-coupon" className="block text-xs text-gray-600 mb-1">Coupon (optional)</label>
            <input id="lb-coupon" className="border rounded px-3 py-2 w-full" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2 mb-2">
          <button className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60" onClick={handleGenerateLink} disabled={disabled}>Generate Link</button>
        </div>
        {linkResult && (
          <div className="text-sm">
            <span className="text-gray-600 mr-2">Generated:</span>
            <a className="text-blue-600 hover:underline" href={linkResult} target="_blank" rel="noreferrer">{linkResult}</a>
          </div>
        )}
      </div>

      {/* Coupons */}
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Coupons</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
          <div>
            <label htmlFor="cp-code" className="block text-xs text-gray-600 mb-1">Code</label>
            <input id="cp-code" className="border rounded px-3 py-2 w-full" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value })} />
          </div>
          <div>
            <label htmlFor="cp-desc" className="block text-xs text-gray-600 mb-1">Description</label>
            <input id="cp-desc" className="border rounded px-3 py-2 w-full" value={couponForm.description || ''} onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })} />
          </div>
          <div>
            <label htmlFor="cp-type" className="block text-xs text-gray-600 mb-1">Type</label>
            <select id="cp-type" className="border rounded px-3 py-2 w-full" value={couponForm.discount_type} onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value as any })}>
              <option value="percent">Percent</option>
              <option value="amount">Amount</option>
            </select>
          </div>
          <div>
            <label htmlFor="cp-value" className="block text-xs text-gray-600 mb-1">Value</label>
            <input id="cp-value" type="number" className="border rounded px-3 py-2 w-full" value={couponForm.discount_value || 0} onChange={(e) => setCouponForm({ ...couponForm, discount_value: Number(e.target.value) })} />
          </div>
          <label className="inline-flex items-center gap-2 text-sm mt-6">
            <input type="checkbox" checked={!!couponForm.active} onChange={(e) => setCouponForm({ ...couponForm, active: e.target.checked })} />
            <span>Active</span>
          </label>
        </div>
        <div className="flex gap-2 mb-4">
          <button className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60" onClick={handleSaveCoupon} disabled={disabled}>Save Coupon</button>
        </div>
        <ul className="divide-y">
          {coupons.length === 0 && <li className="py-2 text-sm text-gray-500">No coupons yet</li>}
          {coupons.map((c) => (
            <li key={c.id} className="py-2 flex items-center justify-between">
              <div className="text-sm">
                <div className="font-medium">{c.code}</div>
                <div className="text-gray-500">{c.description}</div>
              </div>
              <button className="px-2 py-1 rounded border text-sm" onClick={() => handleDeleteCoupon(c.id)}>Delete</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CreativesManagement;
